﻿import { useEffect, useRef, useState, type FC, type MouseEvent, type ReactNode } from "react";
import { CalendarToday, Favorite, FavoriteBorder, LocationOn, People, Share } from "@mui/icons-material";
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import {
    Avatar,
    Box,
    Button,
    ButtonBase,
    Card,
    CardContent,
    CardMedia,
    Chip,
    IconButton,
    Skeleton,
    Stack,
    SxProps,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    alpha,
    useTheme,
} from "@mui/material";
import { ContentCopy, Facebook, LinkedIn, Instagram } from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import { useLocation, useNavigate } from "react-router";
import { IEventList } from "../../../interfaces/IEventList";
import {
    formatFullDateRange,
    formatEventLocationSummary,
    truncateText
} from "../../../utils/formatters";
import { resolveEventAvailability } from "../../../utils/eventAvailability";
import { formatCurrencyAmount } from '../../../utils/localToCurrency';
import { paths } from "../../../routes/paths";
import { useEventShare } from "../../../hooks/useEventShare";
import { useNotifications } from '@toolpad/core';
import { isEventShareable } from "../../../utils/eventShareability";
import { useApiClient } from "../../../ApiClientProvider";
import { buildEventImageUrl, isProtectedEventImageRoute as checkProtectedRoute } from "../../../utils/eventImageUtils";
import { EventVisibilityTag } from "../../Events/Shared/EventVisibilityTag";

// Grid card image width in pixels — matches the card thumbnail at common breakpoints.
const CARD_IMAGE_WIDTH = 480;

// Cards at or below this index are considered above-the-fold on initial load
// (3 cards per row × 1 visible row on most viewports).
const ABOVE_FOLD_THRESHOLD = 3;

interface EventGridVariantProps {
    variant?: 'card';
    event: IEventList;
    onToggleFavourite: (eventId: string) => void;
    /** 0-based position in the listing; drives lazy vs eager loading strategy. */
    index?: number;
    sx?: SxProps;
}

interface EventRowVariantProps {
    variant: 'row';
    event: IEventList;
    onClick: () => void;
    /** Overrides the default category chip; pass null to hide it (e.g. when the source data has no real category). */
    categoryChip?: ReactNode | null;
    /** Overrides the default waitlist/availability chip; pass null to hide it (e.g. when live availability isn't known). */
    statusChip?: ReactNode | null;
}

interface EventCompactVariantProps {
    variant: 'compact';
    title: string;
    imageUrl?: string | null;
    subtitle?: string;
    onClick?: () => void;
    /** Optional trailing element, e.g. an "Open" button. */
    action?: ReactNode;
    thumbnailSize?: number;
}

export type EventCardProps = EventGridVariantProps | EventRowVariantProps | EventCompactVariantProps;

/** Grid card — image, favourite button, share menu, title, date range, location, category/price, registration status. */
const EventGridVariant = ({ event, onToggleFavourite, index = 0, sx }: EventGridVariantProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const apiClient = useApiClient("applicationClient");
    const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
    const { copyLink, shareToFacebook, shareToLinkedIn, shareToX, shareToInstagram, isLoading } = useEventShare();
    const notifications = useNotifications();

    const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
    const [resolvedProtectedImage, setResolvedProtectedImage] = useState<{ source: string; url: string } | null>(null);
    // For below-fold cards, we defer the blob-fetch until the card enters the viewport.
    const [isVisible, setIsVisible] = useState(index < ABOVE_FOLD_THRESHOLD);
    const cardRef = useRef<HTMLDivElement>(null);

    const isBelowFold = index >= ABOVE_FOLD_THRESHOLD;
    const loadingAttr: 'eager' | 'lazy' = isBelowFold ? 'lazy' : 'eager';

    const rawImageUrl = typeof event.imageUrl === 'string' ? event.imageUrl.trim() : '';
    // Append ?w= for proxy routes so the backend returns a resized thumbnail.
    const baseImageUrl = buildEventImageUrl(rawImageUrl, CARD_IMAGE_WIDTH) ?? rawImageUrl;
    const isProtectedRoute = checkProtectedRoute(rawImageUrl);
    const resolvedImageUrl = isProtectedRoute
        ? (resolvedProtectedImage?.source === baseImageUrl ? resolvedProtectedImage.url : null)
        : (baseImageUrl || null);
    const hasDisplayImage = Boolean(resolvedImageUrl && failedImageUrl !== resolvedImageUrl);

    // Use IntersectionObserver to defer blob-fetching for below-fold cards.
    useEffect(() => {
        if (!isBelowFold || !cardRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' },
        );
        observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, [isBelowFold]);

    useEffect(() => {
        if (!isProtectedRoute || !baseImageUrl || !isVisible) {
            return;
        }

        let isActive = true;
        let objectUrl: string | null = null;

        const resolveProtectedImage = async () => {
            try {
                const response = await apiClient.get<Blob>(baseImageUrl, { responseType: 'blob' });
                const url = URL.createObjectURL(response.data);

                if (isActive) {
                    objectUrl = url;
                    setResolvedProtectedImage({ source: baseImageUrl, url });
                } else {
                    // Effect was cleaned up while the request was in flight — revoke immediately, nothing will reference it.
                    URL.revokeObjectURL(url);
                }
            } catch {
                if (isActive) {
                    setResolvedProtectedImage(null);
                }
            }
        };

        resolveProtectedImage();

        return () => {
            isActive = false;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [apiClient, baseImageUrl, isProtectedRoute, isVisible]);

    const availability = resolveEventAvailability({
        ticketAvailability: event.ticketAvailability,
        registrationDeadline: event.registrationDeadline?.toString(),
        endDate: event.endDate.toString(),
        endUtc: event.endUtc,
    });
    // Unknown availability and the plain "available" state stay unbadged on the grid.
    const availabilityBadge = availability.label && availability.kind !== 'available' ? availability : null;
    const isShareable = isEventShareable(event.status);
    const isShareDisabled = isLoading || !isShareable;

    const handleNavigate = () => {
        const currentPath = location.pathname;
        if (currentPath == paths.public.myEvents) {
            navigate(`/events/${event.id}`)
        } else {
            const newPath =
                currentPath === "/" ? `/${event.id}` : `${currentPath}/${event.id}`;
            navigate(newPath);
        }
    };

    const handleShareClick = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setShareAnchorEl(e.currentTarget);
    };

    const handleShareClose = () => {
        setShareAnchorEl(null);
    };

    const handleCopyLink = async () => {
        handleShareClose();
        const success = await copyLink({ eventId: event.id, eventTitle: event.title });
        if (success) {
            notifications.show('Event link copied!', { severity: 'success', autoHideDuration: 2000 });
        }
    };

    const handleShareFacebook = async () => {
        handleShareClose();
        await shareToFacebook({ eventId: event.id, eventTitle: event.title });
    };

    const handleShareLinkedIn = async () => {
        handleShareClose();
        await shareToLinkedIn({ eventId: event.id, eventTitle: event.title });
    };

    const handleShareX = async () => {
        handleShareClose();
        await shareToX({ eventId: event.id, eventTitle: event.title });
    };

    const handleShareInstagram = async () => {
        handleShareClose();
        const success = await shareToInstagram({ eventId: event.id, eventTitle: event.title });
        if (success) {
            notifications.show('Link copied! Paste in Instagram.', { severity: 'info', autoHideDuration: 4000 });
        }
    };

    return (
        <>
            <Card
                ref={cardRef}
                onClick={handleNavigate}
                sx={[
                    {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 1.5,
                        transition: 'box-shadow 0.2s ease',
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                    }}
                >
                    <Box sx={{ position: "relative" }}>
                        {hasDisplayImage ? (
                            <CardMedia
                                component="img"
                                // Explicit height + width prevent layout shift (CLS).
                                height="120"
                                image={resolvedImageUrl ?? undefined}
                                alt={event.title}
                                title={event.title}
                                loading={loadingAttr}
                                onError={() => {
                                    if (resolvedImageUrl) {
                                        setFailedImageUrl(resolvedImageUrl);
                                    }
                                }}
                                sx={{ objectFit: 'cover', width: '100%', aspectRatio: '16/5' }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    height: 120,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'action.hover',
                                    color: 'text.disabled',
                                }}
                            >
                                <CalendarToday sx={{ fontSize: 40, opacity: 0.4 }} />
                            </Box>
                        )}
                        <IconButton
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 48,
                                width: 44,
                                height: 44,
                                color: 'text.primary',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 7,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    transition: 'background-color 0.2s ease',
                                },
                                '& .MuiSvgIcon-root': {
                                    position: 'relative',
                                    zIndex: 1,
                                    fontSize: 16,
                                },
                                '&:hover::before': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavourite(event.id || "");
                            }}
                        >
                            {event.isFavourite ? (
                                <Favorite sx={{ color: '#d32f2f', fontSize: 16 }} />
                            ) : (
                                <FavoriteBorder sx={{ fontSize: 16 }} />
                            )}
                        </IconButton>

                        <Tooltip title={isShareable ? 'Share event' : 'Share is available when the event is Scheduled, In Progress, or Completed'}>
                            <span>
                                <IconButton
                                    aria-disabled={isShareDisabled}
                                    disableRipple={isShareDisabled}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 44,
                                        height: 44,
                                        color: isShareDisabled ? 'rgba(255, 255, 255, 0.86)' : 'inherit',
                                        cursor: isShareDisabled ? 'not-allowed' : 'pointer',
                                        pointerEvents: 'auto',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 7,
                                            borderRadius: '50%',
                                            backgroundColor: isShareDisabled ? 'rgba(31, 41, 55, 0.72)' : 'rgba(255, 255, 255, 0.94)',
                                            border: isShareDisabled
                                                ? '1px solid rgba(255, 255, 255, 0.28)'
                                                : '1px solid rgba(73, 49, 147, 0.18)',
                                            boxShadow: isShareDisabled
                                                ? '0 4px 12px rgba(15, 23, 42, 0.38)'
                                                : '0 3px 8px rgba(15, 23, 42, 0.18)',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            position: 'relative',
                                            zIndex: 1,
                                            fontSize: 15,
                                            opacity: isShareDisabled ? 0.9 : 1,
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    onClick={(e) => {
                                        if (isShareDisabled) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return;
                                        }

                                        handleShareClick(e);
                                    }}
                                >
                                    <Share />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <CardContent sx={{ flex: 1, pb: 1, px: 2, pt: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {truncateText(event.title ?? '', 50)}
                            </Typography>
                            <EventVisibilityTag
                                isInviteOnly={event.isInviteOnly}
                                isEventPublic={event.isEventPublic}
                                sx={{
                                    height: 24,
                                    alignSelf: 'flex-start',
                                    ml: 1,
                                }}
                            />
                        </Box>

                        {/* Date */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                {event.startDate && event.endDate
                                    ? formatFullDateRange(event.startDate.toString(), event.endDate.toString(), event.isSingleDay)
                                    : (event.startDate.toString())}
                            </Typography>
                        </Box>

                        {/* Location */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                            <LocationOn sx={{ fontSize: 13, color: 'text.secondary' }} />
                            {(event.locationCity?.length ?? 0) > 0 && (event.locationNames?.length ?? 0) > 0 ? (
                                <Tooltip
                                    title={
                                        <Box>
                                            {event.locationCity?.map((city, index) => (
                                                <Typography key={index} variant="body2">
                                                    {city} – {event.locationNames?.[index]}
                                                </Typography>
                                            ))}
                                        </Box>
                                    }
                                    arrow
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ cursor: (event.locationCity?.length ?? 0) > 1 ? "pointer" : "default" }}
                                    >
                                        {event.locationCity?.[0]}
                                        {(event.locationCity?.length ?? 0) > 1 && ` +${(event.locationCity?.length ?? 0) - 1} more`}
                                    </Typography>
                                </Tooltip>
                            ) : (
                                <Typography variant="caption" color="text.secondary">Location TBD</Typography>
                            )}
                        </Box>

                        {/* Category */}
                        {(event.categoryName || event.subCategoryName) && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                                {[event.categoryName, event.subCategoryName].filter(Boolean).join(' · ')}
                            </Typography>
                        )}

                        {/* Tags + attendees chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                            <Chip
                                icon={<People sx={{ fontSize: '12px !important' }} />}
                                label={`${event.attendees || 0} attendees`}
                                size="small"
                                sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#f0f0f0', color: 'text.primary' }}
                            />
                            {event?.tags?.slice(0, 2).map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 22, fontSize: '0.72rem' }}
                                />
                            ))}
                            {event?.tags?.length > 2 && (
                                <Tooltip title={event.tags.slice(2).join(", ")}>
                                    <Chip
                                        label={`+${event.tags.length - 2}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: '0.72rem' }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </CardContent>
                </Box>

                {/* Badges */}
                {availabilityBadge && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            backgroundColor: availabilityBadge.tone === 'neutral'
                                ? 'action.disabledBackground'
                                : `${availabilityBadge.tone}.light`,
                            color: availabilityBadge.tone === 'neutral'
                                ? 'text.secondary'
                                : `${availabilityBadge.tone}.contrastText`,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                        }}
                    >
                        {availabilityBadge.label}
                    </Box>
                )}

            </Card>

            <Menu
                anchorEl={shareAnchorEl}
                onClose={handleShareClose}
                open={Boolean(shareAnchorEl)}
            >
                <MenuItem onClick={handleCopyLink} disabled={isLoading}>
                    <ListItemIcon>
                        <ContentCopy fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copy Link</ListItemText>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={handleShareFacebook} disabled={isLoading}>
                    <ListItemIcon>
                        <Facebook fontSize="small" sx={{ color: '#1877F2' }} />
                    </ListItemIcon>
                    <ListItemText>Facebook</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleShareLinkedIn} disabled={isLoading}>
                    <ListItemIcon>
                        <LinkedIn fontSize="small" sx={{ color: '#0A66C2' }} />
                    </ListItemIcon>
                    <ListItemText>LinkedIn</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleShareX} disabled={isLoading}>
                    <ListItemIcon>
                        <XIcon fontSize="small" sx={{ color: '#000000' }} />
                    </ListItemIcon>
                    <ListItemText>X</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleShareInstagram} disabled={isLoading}>
                    <ListItemIcon>
                        <Instagram fontSize="small" sx={{ color: '#E4405F' }} />
                    </ListItemIcon>
                    <ListItemText>Instagram</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

/** Bordered list row — thumbnail, title, date/location/price, category+status chips, "View" button. */
const EventRowVariant = ({ event, onClick, categoryChip, statusChip }: EventRowVariantProps) => {
    const theme = useTheme();

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(keyEvent) => {
                if (keyEvent.target !== keyEvent.currentTarget) return;
                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                    keyEvent.preventDefault();
                    onClick();
                }
            }}
            sx={{ py: 1.5, cursor: 'pointer', borderRadius: 1, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
        >
            <Box
                sx={{
                    width: 84,
                    height: 56,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    background: event.imageUrl
                        ? `url(${event.imageUrl}) center/cover`
                        : `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                }}
            />
            <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }}>
                <Typography variant="subtitle2" noWrap>{event.title}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {new Date(event.startDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · {formatEventLocationSummary(event) || 'Online'} · {event.price > 0 ? `from ${formatCurrencyAmount(event.price, { minimumFractionDigits: Number.isInteger(event.price) ? 0 : 2, maximumFractionDigits: Number.isInteger(event.price) ? 0 : 2 })}` : 'Free'}
                </Typography>
            </Box>
            {categoryChip !== null && (
                categoryChip ?? (
                    <Chip
                        label={event.categoryName || 'Social'}
                        size="small"
                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0, bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}
                    />
                )
            )}
            {statusChip !== null && (
                statusChip ?? (() => {
                    const availability = resolveEventAvailability({
                        ticketAvailability: event.ticketAvailability,
                        registrationDeadline: event.registrationDeadline,
                        endDate: event.endDate.toString(),
                        endUtc: event.endUtc,
                    });

                    // Waitlist state is user-specific, so it only overrides a sold-out event.
                    const label = availability.kind === 'soldOut' && event.isWaitlisted
                        ? 'Sold out · on waitlist'
                        : availability.kind === 'soldOut' && availability.canJoinWaitlist
                            ? 'Sold out · waitlist open'
                            : availability.label;

                    if (!label) return null;

                    const palette = availability.tone === 'neutral' ? null : availability.tone;

                    return (
                        <Chip
                            label={label}
                            size="small"
                            sx={{
                                flexShrink: 0,
                                maxWidth: { xs: 112, sm: 'none' },
                                bgcolor: palette ? alpha(theme.palette[palette].main, 0.1) : 'action.disabledBackground',
                                color: palette ? `${palette}.dark` : 'text.secondary',
                            }}
                        />
                    );
                })()
            )}
            <Button variant="outlined" size="small" onClick={(clickEvent) => { clickEvent.stopPropagation(); onClick(); }} sx={{ borderRadius: 999, flexShrink: 0 }}>View</Button>
        </Stack>
    );
};

/** Thumbnail + title/subtitle row, used in sidebars, previews and search results. */
const EventCompactVariant = ({ title, imageUrl, subtitle, onClick, action, thumbnailSize = 32 }: EventCompactVariantProps) => {
    const thumbnail = imageUrl ? (
        <Box
            component="img"
            src={imageUrl}
            alt={title}
            loading="lazy"
            sx={{ width: thumbnailSize, height: thumbnailSize, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider', flexShrink: 0 }}
        />
    ) : (
        <Avatar variant="rounded" sx={{ width: thumbnailSize, height: thumbnailSize, bgcolor: 'primary.lighter', color: 'primary.main' }}>
            <CalendarMonth sx={{ fontSize: thumbnailSize * 0.55 }} />
        </Avatar>
    );

    const content = (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            {thumbnail}
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{title}</Typography>
                {subtitle && <Typography variant="caption" color="text.secondary" noWrap display="block">{subtitle}</Typography>}
            </Box>
        </Stack>
    );

    return (
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
            {onClick ? (
                <ButtonBase onClick={onClick} sx={{ minWidth: 0, flex: 1, justifyContent: 'flex-start', borderRadius: 1, textAlign: 'left', p: 0.25 }}>
                    {content}
                </ButtonBase>
            ) : content}
            {action}
        </Stack>
    );
};

/** Event tile/row — single source of truth for event summaries: an image-banner grid card (`variant="card"`, default), a bordered list row (`variant="row"`), or a thumbnail row for sidebars/previews (`variant="compact"`). */
const EventCard = (props: EventCardProps) => {
    if (props.variant === 'row') return <EventRowVariant {...props} />;
    if (props.variant === 'compact') return <EventCompactVariant {...props} />;
    return <EventGridVariant {...props} />;
};

export interface EventCardSkeletonProps {
    variant?: 'card' | 'row' | 'compact';
    /** `compact` variant only. */
    thumbnailSize?: number;
}

/** Placeholder matching EventCard's shape, shown while an events list/grid is loading. */
export const EventCardSkeleton: FC<EventCardSkeletonProps> = ({ variant = 'card', thumbnailSize = 32 }) => {
    if (variant === 'row') {
        return (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1.5 }}>
                <Skeleton variant="rounded" width={84} height={56} sx={{ flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Skeleton variant="text" width="55%" sx={{ fontSize: '0.95rem' }} />
                    <Skeleton variant="text" width="75%" sx={{ fontSize: '0.75rem' }} />
                </Box>
                <Skeleton variant="rounded" width={72} height={24} sx={{ display: { xs: 'none', sm: 'block' }, borderRadius: 999 }} />
                <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: 999 }} />
            </Stack>
        );
    }

    if (variant === 'compact') {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="rounded" width={thumbnailSize} height={thumbnailSize} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Skeleton variant="text" width="65%" />
                    <Skeleton variant="text" width="45%" />
                </Box>
            </Stack>
        );
    }

    return (
        <Card variant="outlined" sx={{ height: '100%', borderRadius: 1.5, overflow: 'hidden' }}>
            <Skeleton variant="rectangular" height={120} />
            <CardContent sx={{ pb: 1, px: 2, pt: 1.5 }}>
                <Skeleton variant="text" width="70%" sx={{ fontSize: '1.05rem' }} />
                <Skeleton variant="text" width="50%" sx={{ fontSize: '0.75rem' }} />
                <Skeleton variant="text" width="40%" sx={{ fontSize: '0.75rem' }} />
            </CardContent>
        </Card>
    );
};

export default EventCard;
